import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CaasRoutesService } from './caas-routes.service';
import {
  CaasFormField,
  CaasIcon,
  CaasLink,
  CaasLinkOptional,
  CaasSimpleField,
} from '../shared/interfaces/caas/caas.interface';
import { Video } from '../shared/interfaces/video.interface';
import LinkType from '../shared/enums/link-type.enums';
import { ButtonType } from '../shared/enums/button-type.enums';
import { HrefType } from '../shared/enums/href-type.enums';

interface CustomAnchorElement extends HTMLAnchorElement {
  __handleClick?: EventListener;
}

@Injectable({
  providedIn: 'root',
})
export class CaasBodytextService {
  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private sanitizer: DomSanitizer,
    private caasRoutesService: CaasRoutesService
  ) {}

  /**
   * Retrieves the icon value from a CaasIcon object.
   */
  getIcon(icon: CaasIcon): string {
    return icon?.value?.toString() || '';
  }

  /**
   * Generates a Video object based on provided video type and links.
   */
  getVideo(
    videoType: CaasFormField,
    vimeoLink: CaasSimpleField,
    youtubeLink: CaasSimpleField
  ): Video {
    return {
      videoSrc: this.getVideoSrc(videoType, vimeoLink, youtubeLink),
      videoId: this.getVideoId(videoType, vimeoLink, youtubeLink),
      thumbnailSrc: '', // Placeholder for future thumbnail implementation.
    };
  }

  /**
   * Extracts the video ID based on video type.
   */
  getVideoId(
    videoType: CaasFormField,
    vimeoLink: CaasSimpleField,
    youtubeLink: CaasSimpleField
  ): string {
    switch (videoType?.value?.identifier) {
      case 'vimeo':
        return vimeoLink?.value || '';
      case 'youtube':
        return youtubeLink?.value || '';
      default:
        return '';
    }
  }

  /**
   * Constructs the video source URL based on type.
   */
  getVideoSrc(
    videoType: CaasFormField,
    vimeoLink: CaasSimpleField,
    youtubeLink: CaasSimpleField
  ): SafeResourceUrl {
    const baseUrls: Record<string, string> = {
      vimeo: 'https://player.vimeo.com/video/',
      youtube: 'https://www.youtube.com/embed/',
    };

    const videoId = this.getVideoId(videoType, vimeoLink, youtubeLink);

    if (!videoId) {
      console.warn('Missing video ID for type:', videoType?.value?.identifier);
      return this.sanitizer.bypassSecurityTrustResourceUrl('');
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `${baseUrls[videoType?.value?.identifier] || ''}${videoId}`
    );
  }

  removeListContent(htmlString?: string | null) {
    if (!htmlString) return undefined;
    // Regular expression to match content between <p><ul> and </ul></p>
    const regex = /<p>\s*<ul[^>]*>.*?<\/ul>\s*<\/p>/gs;

    // Replace matched content with an empty string
    return htmlString.replace(regex, '');
  }

  getButtonLayout(
    link?: CaasLink,
    linkType?: CaasFormField
  ): ButtonType | LinkType {
    if (linkType) {
      switch (linkType?.value?.identifier) {
        // Components that have the possibility to switch between CTA and Downloads in Firstspirit.
        // Downloads allowed cross referencing to have more than one download link.
        case 'cta':
          if (link) {
            return this.checkButtonLayout(link);
          }
          return LinkType.DOWNLOAD_NO_UNDERSCORED;
        case 'downloads':
          return LinkType.DOWNLOAD_NO_UNDERSCORED;
      }
    } else {
      // Components which not have the possibility to switch between CTA and Downloads in Firstspirit.
      // For example prefooter.
      if (link && link?.value?.formData?.lt_button_layout) {
        return this.checkButtonLayout(link);
      } else if (link && link?.value?.formData?.lt_link_type) {
        return this.checkLinksType(link);
      }
    }
    return LinkType.DOWNLOAD_NO_UNDERSCORED;
  }

  checkButtonLayout(link: CaasLink): ButtonType | LinkType {
    switch (link?.value?.formData?.lt_button_layout?.value?.identifier) {
      case 'primary':
        return ButtonType.PRIMARY;
      case 'secondary':
        return ButtonType.SECONDARY;
      case 'tertiary':
        return ButtonType.TERTIARY;
      case 'textlink':
        return this.checkLinksType(link);
      default:
        return ButtonType.PRIMARY;
    }
  }

  checkLinksType(link: CaasLink): LinkType {
    switch (link?.value?.formData?.lt_link_type?.value?.identifier) {
      case 'internal':
        return LinkType.PRIMARY_ARROW;
      case 'external':
        return LinkType.EXTERNAL;
      case 'video':
        return LinkType.VIDEO_LINK_NO_UNDERSCORE;
      case 'download':
        return LinkType.DOWNLOAD_NO_UNDERSCORED;
      default:
        return LinkType.PRIMARY_ARROW;
    }
  }

  checkOptionalLinksType(link: CaasLinkOptional): LinkType {
    switch (link?.value?.formData?.lt_link_type?.value?.identifier) {
      case 'internal':
        return LinkType.PRIMARY_ARROW;
      case 'external':
        return LinkType.EXTERNAL;
      case 'video':
        return LinkType.VIDEO_LINK_NO_UNDERSCORE;
      case 'download':
        return LinkType.DOWNLOAD_NO_UNDERSCORED;
      default:
        return LinkType.PRIMARY_ARROW;
    }
  }

  getHrefType(link: CaasLink | CaasLinkOptional): HrefType {
    if (link?.value) {
      switch (link?.value.formData?.lt_link_type?.value.identifier) {
        case 'internal':
          return HrefType.ANGULAR_ROUTE;
        case 'external':
          return HrefType.EXTERNAL;
        case 'video':
          return HrefType.OPEN_MODAL_VIDEO;
        case 'download':
          return HrefType.EXTERNAL;
        default:
          return HrefType.ANGULAR_ROUTE;
      }
    }
    return HrefType.ANGULAR_ROUTE;
  }

  getFragment(link?: CaasLink): string {
    return (
      link?.value?.formData?.st_anchor_name?.value
        ?.toString()
        .trim()
        .toLowerCase() || ''
    );
  }

  getHref(link: CaasLink | CaasLinkOptional): string {
    if (link.value) {
      if (link.value?.formData?.lt_link_type?.value.identifier === 'internal') {
        return (
          this.caasRoutesService.getSeoRouteByIdentifier(
            link.value?.formData?.lt_link_internal?.value?.identifier ?? '',
            this.router.config
          )?.path ?? '/404'
        );
      } else {
        return link?.value?.formData?.lt_link_external?.value ?? '/404';
      }
    } else {
      return '/404';
    }
  }

  foundHref(link: CaasLink | CaasLinkOptional): string {
    if (link.value) {
      if (link.value?.formData?.lt_link_type?.value.identifier === 'internal') {
        return (
          this.caasRoutesService.getSeoRouteByIdentifier(
            link.value?.formData?.lt_link_internal?.value?.identifier ?? '',
            this.router.config
          )?.path ?? '/404'
        );
      } else {
        return link?.value?.formData?.lt_link_external?.value ?? '/404';
      }
    } else {
      return '/404';
    }
  }
  /**
   * Extracts and formats the text editor content, replacing links and lists with correct formatting.
   * @param bodytext The raw content from the text editor.
   * @param injectedClass Optional custom styles for links.
   * @returns Formatted HTML content with sanitized links and lists.
   */
  extractTextEditorContent(bodytext: string, injectedClass?: string): string {
    if (!bodytext) return '';

    const textWithLinks = this.formatLinks(bodytext, injectedClass);
    const textWithLists = this.formatLists(textWithLinks);

    return textWithLists
      ? (this.sanitizer.bypassSecurityTrustHtml(textWithLists) as string)
      : bodytext;
  }
  /**
   * Decodes and sanitizes URLs or strings for safe rendering.
   */
  private sanitizeAndDecode(value: string): string {
    try {
      const decoded = decodeURIComponent(JSON.parse(`"${value}"`));
      return this.sanitizer.sanitize(1, decoded) || decoded;
    } catch {
      return this.sanitizer.sanitize(1, value.replace(/\\\//g, '/')) || value;
    }
  }
  /**
   * Creates an anchor tag with sanitized attributes.
   */
  private createAnchorTag(
    href: string,
    label: string,
    title: string,
    type: string,
    additionalClass?: string,
    openInNewTab: boolean = false,
    fragment?: string
  ): string {
    const sanitizedHref = this.sanitizeAndDecode(href);
    const fragmentPart = fragment ? `#${fragment}` : '';
    const target = openInNewTab
      ? ' target="_blank" rel="noopener noreferrer"'
      : '';
    return `<a class="bodytext-link bodytext-link--${type} ${additionalClass || ''}" 
              href="${sanitizedHref}${fragmentPart}" 
              title="${this.sanitizeAndDecode(title || label)}" 
              aria-label="${this.sanitizeAndDecode(label)}"${target}>
              ${this.sanitizeAndDecode(label || title)}</a>`;
  }

  /**
   * Formats links within the bodytext content for Vimeo, YouTube, and Downloads.
   * Handles internal, external, video, and download links appropriately.
   */
  formatLinks(bodytext: string, injectedClass?: string): string {
    const scriptRegex = /<div[\s\S]*?<\/div>/gi;

    return bodytext?.replace(scriptRegex, (match) => {
      // Extract fields using utility functions and trim them
      const linkType = this.extractFromMatch(
        /"lt_link_type".*?"label":"(.*?)"/,
        match
      );

      const linkLabel =
        this.extractFromMatch(
          /"lt_link_label".*?"value":"(.*?)"/,
          match
        )?.trim() || '';

      const linkTitle =
        this.extractFromMatch(
          /"lt_link_title".*?"value":"(.*?)"/,
          match
        )?.trim() || '';

      const internalLink = this.extractFromMatch(
        /"lt_link_internal","value":{(.*?)}/,
        match
      );

      const fragment =
        this.extractFromMatch(
          /"lt_anchor_name".*?"value":"(.*?)"/,
          match
        )?.trim() ||
        this.extractFromMatch(
          /"st_anchor_name".*?"value":"(.*?)"/,
          match
        )?.trim() ||
        '';

      const externalLink = this.extractFromMatch(
        /"lt_link_external".*?"value":"(.*?)"/,
        match
      )?.trim();

      const videoType = this.extractFromMatch(
        /"lt_link_video_type".*?"label":"(.*?)"/,
        match
      );

      const vimeoId = this.extractFromMatch(
        /"lt_link_vimeo_id".*?"value":"(.*?)"/,
        match
      )?.trim();

      const youtubeId = this.extractFromMatch(
        /"lt_link_youtube_id".*?"value":"(.*?)"/,
        match
      )?.trim();

      const downloadLink = this.extractFromMatch(
        /"lt_link_external".*?"value":"(.*?)"/,
        match
      )?.trim();
      // console.log('Debug > formatLinks > Extracted Data:', {
      //   linkType,
      //   linkLabel,
      //   linkTitle,
      //   internalLink,
      //   externalLink,
      //   videoType,
      //   vimeoId,
      //   youtubeId,
      //   downloadLink,
      // });

      switch (linkType) {
        case 'Interner Link': {
          const parsedInternalLink = internalLink
            ? JSON.parse(`{${internalLink}}`)
            : null;
          const route = this.caasRoutesService.getSeoRouteByIdentifier(
            parsedInternalLink?.identifier || '',
            this.router.config
          );
          return this.createAnchorTag(
            route?.path || '/404',
            linkLabel,
            linkTitle,
            'internal',
            injectedClass,
            false,
            fragment || undefined
          );
        }

        case 'Externer Link': {
          if (!externalLink) {
            console.warn('Missing external link value');
            return match;
          }
          return this.createAnchorTag(
            externalLink,
            linkLabel,
            linkTitle,
            'external',
            injectedClass,
            true
          );
        }

        case 'Video-Link': {
          const baseUrls: Record<string, string> = {
            Vimeo: 'https://player.vimeo.com/video/',
            Youtube: 'https://www.youtube.com/embed/',
          };
          const videoId = videoType === 'Vimeo' ? vimeoId : youtubeId;
          if (!videoId) {
            console.warn('Missing video ID for video type:', videoType);
            return match;
          }
          return this.createAnchorTag(
            `${baseUrls[videoType || '']}${videoId}`,
            linkLabel,
            linkTitle,
            'video',
            injectedClass,
            true
          );
        }

        case 'Download-Link': {
          if (!downloadLink) {
            console.warn('Missing download link value');
            return match;
          }
          return this.createAnchorTag(
            downloadLink,
            linkLabel,
            linkTitle,
            'download',
            injectedClass,
            true
          );
        }

        default:
          console.warn('Unrecognized link type:', linkType);
          return match; // Fallback to original match
      }
    });
  }

  /**
   * Utility function to extract data using regex.
   */
  private extractFromMatch(regex: RegExp, content: string): string | null {
    return regex.exec(content)?.[1] || null;
  }

  /**
   * Formats lists within the bodytext content.
   */
  formatLists(bodytext: string): string {
    const listRegex = /<ul[^>]*>[\s\S]*?<\/ul>/gi; // Match all <ul> blocks

    return bodytext?.replace(listRegex, (match) => {
      // Flatten all <li> items
      const flattenedListItems = this.flattenListItems(match)
        ?.map((li) => `<li>${li}</li>`)
        .join('');

      return flattenedListItems
        ? `<ul class="list-checkmark">${flattenedListItems}</ul>`
        : '';
    });
  }

  /**
   * Flattens all <li> elements in the given HTML string into a flat array of text content.
   * Uses DOM parsing for robustness.
   * @param htmlString The HTML string containing <ul> and <li> elements.
   * @returns A flat array of <li> text content.
   */
  flattenListItems(htmlString: string): string[] {
    const listItems: string[] = [];

    // Create a temporary DOM element to parse the HTML string
    const tempDiv = this.document.createElement('div');
    tempDiv.innerHTML = htmlString;

    /**
     * Recursively traverse <ul> and <li> elements to extract text and preserve desired HTML tags.
     * @param ul The current <ul> element to traverse.
     */
    const traverseUl = (ul: HTMLElement) => {
      // Select only direct <li> children of the current <ul>
      const liElements = ul.querySelectorAll(':scope > li');
      liElements.forEach((li) => {
        const liClone = li.cloneNode(true) as HTMLElement;

        // Remove any nested <ul> elements from the clone
        const nestedUls = liClone.querySelectorAll('ul');
        nestedUls.forEach((nestedUl) => nestedUl.remove());

        // Get the innerHTML of the cleaned <li>
        const itemHtml = liClone.innerHTML.trim();
        if (itemHtml) {
          listItems.push(itemHtml);
        }

        // Check if the original <li> contains a nested <ul>
        const originalNestedUl = li.querySelector(':scope > ul');
        if (originalNestedUl) {
          traverseUl(originalNestedUl as HTMLElement);
        }
      });
    };

    // Select only root <ul> elements (direct children of tempDiv)
    const rootUlElements = tempDiv.querySelectorAll(':scope > ul');
    rootUlElements.forEach((rootUl) => {
      traverseUl(rootUl as HTMLElement);
    });

    // console.log('Debug > flattenListItems > Final Flattened Items:', listItems);
    return listItems;
  }

  /**
   * Extracts content of list items for further processing. MULTIPLE UL
   */
  // extractListContent(
  //   htmlString: string,
  //   openingTag: string,
  //   closingTag: string
  // ): { li: string }[] | null {
  //   const ulContent = /<ul[^>]*>(.*?)<\/ul>/gs.exec(htmlString)?.[1];
  //   if (!ulContent) return null;

  //   const listItems = [...ulContent.matchAll(/<li[^>]*>(.*?)<\/li>/gs)];
  //   return listItems.map((item) => ({
  //     li: `${openingTag}${item[1]}${closingTag}`,
  //   }));
  // }

  /**
   * Adds event listeners to router links for SPA navigation.
   */
  setRouterLinks(): void {
    const links = this.document.querySelectorAll(
      'a[routerLink]'
    ) as NodeListOf<CustomAnchorElement>;
    links.forEach((link) => {
      const handleClick = (event: Event) => {
        event.preventDefault();
        this.router.navigate([
          (event.currentTarget as HTMLAnchorElement).getAttribute('routerLink'),
        ]);
      };
      link.addEventListener('click', handleClick);
      link.__handleClick = handleClick;
    });
  }

  /**
   * Removes event listeners from router links.
   */
  removeRouterLinks(): void {
    const links = this.document.querySelectorAll(
      'a[routerLink]'
    ) as NodeListOf<CustomAnchorElement>;
    links.forEach((link) => {
      if (link.__handleClick) {
        link.removeEventListener('click', link.__handleClick);
      }
    });
  }
}
